import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import PayPalPayment from '@/components/PayPalPayment';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Lock, Upload, Loader2, DollarSign, Video, Music } from 'lucide-react';

const ArtistDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [featureToUnlock, setFeatureToUnlock] = useState(null);
  
  // Transparence des ventes
  const [mySales, setMySales] = useState([]);
  const [showSalesModal, setShowSalesModal] = useState(false);
  
  // Upload form (pour vidéo ET audio)
  const [uploadType, setUploadType] = useState('audio'); // 'audio' ou 'video'
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    artist_name: '',
    price_usd: '' // Seulement pour vidéo
  });
  const [videoFile, setVideoFile] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Modal de paiement au moment de publier
  const [showPublishPaymentModal, setShowPublishPaymentModal] = useState(false);
  const [uploadPending, setUploadPending] = useState(null); // Données de l'upload en attente
  
  // Payment
  const [paymentMethod, setPaymentMethod] = useState('ORANGE_MONEY');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentType, setPaymentType] = useState('bkapay'); // 'bkapay' ou 'paypal'
  
  // Audio upload modal (COLONNE 1)
  const [showAudioPaywall, setShowAudioPaywall] = useState(false);
  const [showAudioUploadModal, setShowAudioUploadModal] = useState(false);
  const [audioForm, setAudioForm] = useState({
    title: '',
    artist_name: ''
  });
  const [audioFile, setAudioFile] = useState(null);
  const [audioCover, setAudioCover] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  
  // Mes Clips uploadés
  const [myVideos, setMyVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchDashboard();
    fetchMyVideos();
  }, [isAuthenticated]);

  const fetchDashboard = async () => {
    try {
      const [dashboardRes, statsRes] = await Promise.all([
        api.get('/artist/dashboard'),
        api.get('/artist/stats')
      ]);
      
      setDashboard({
        ...dashboardRes.data,
        total_likes: statsRes.data.total_likes || 0
      });
    } catch (error) {
      // Si pas artiste/admin, créer des features vides pour montrer les paywalls
      if (error.response?.status === 403) {
        setDashboard({
          distribution_unlocked: false,
          selling_unlocked: false,
          total_videos: 0,
          total_sales: 0,
          total_revenue_usd: 0,
          total_likes: 0,
          pending_payout_usd: 0
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMySales = async () => {
    try {
      const response = await api.get('/artist/sales/my-sales');
      setMySales(response.data.sales || []);
    } catch (error) {
      console.error('Erreur chargement ventes:', error);
    }
  };
  
  const fetchMyVideos = async () => {
    setLoadingVideos(true);
    try {
      const response = await api.get('/artist/videos');
      setMyVideos(response.data || []);
    } catch (error) {
      console.error('Erreur chargement vidéos:', error);
      toast.error('Impossible de charger vos clips');
    } finally {
      setLoadingVideos(false);
    }
  };

  const handleUnlockFeature = (feature) => {
    // Plus de paywall ici - on ouvre directement le modal d'upload
    setUploadType(feature === 'distribution' ? 'audio' : 'video');
    setShowUploadModal(true);
  };
  
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!videoFile) {
      toast.error(uploadType === 'audio' ? 'Sélectionnez un fichier audio' : 'Sélectionnez une vidéo');
      return;
    }
    
    if (uploadType === 'video' && (!uploadForm.price_usd || uploadForm.price_usd < 1)) {
      toast.error('Définissez un prix de vente (min 1$)');
      return;
    }
    
    // Préparer les données pour le paiement
    const uploadData = {
      type: uploadType,
      title: uploadForm.title,
      description: uploadForm.description,
      artist_name: uploadForm.artist_name,
      price_usd: uploadType === 'video' ? uploadForm.price_usd : 15,
      videoFile: videoFile,
      coverImage: coverImage
    };
    
    setUploadPending(uploadData);
    setShowUploadModal(false);
    setShowPublishPaymentModal(true);
  };
  
  const handlePublishAfterPayment = async () => {
    if (!uploadPending) return;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('title', uploadPending.title);
      formData.append('description', uploadPending.description || '');
      formData.append('artist_name', uploadPending.artist_name || user?.full_name || 'Artiste');
      
      if (uploadPending.type === 'video') {
        formData.append('price_usd', uploadPending.price_usd);
        formData.append('video', uploadPending.videoFile);
      } else {
        formData.append('audio', uploadPending.videoFile);
      }
      
      if (uploadPending.coverImage) {
        formData.append('cover', uploadPending.coverImage);
      }
      
      const endpoint = uploadPending.type === 'audio' ? '/videos/upload-audio' : '/videos/upload';
      await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`✅ ${uploadPending.type === 'audio' ? 'Audio' : 'Vidéo'} publié avec succès !`);
      
      // Réinitialiser
      setUploadPending(null);
      setUploadForm({ title: '', description: '', artist_name: '', price_usd: '' });
      setVideoFile(null);
      setCoverImage(null);
      setShowPublishPaymentModal(false);
      fetchDashboard();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de publication');
    } finally {
      setUploading(false);
    }
  };
  
  const handleAddTitleClick = () => {
    // Ouvrir directement le formulaire upload
    setShowAudioUploadModal(true);
  };
  
  const handleAudioPaywallSubmit = async () => {
    if (!phoneNumber) {
      toast.error('Entrez votre numéro');
      return;
    }

    setPaymentLoading(true);
    try {
      await api.post('/artist/unlock-feature', {
        feature_type: 'distribution',
        payment_method: paymentMethod,
        phone_number: phoneNumber
      });
      
      toast.success('Paiement initié ! Confirmez sur votre téléphone');
      
      // Poll for unlock
      setTimeout(() => {
        fetchDashboard();
        setShowAudioPaywall(false);
        setPhoneNumber('');
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur paiement');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentSubmit = async () => {
    if (!phoneNumber) {
      toast.error('Entrez votre numéro');
      return;
    }

    setPaymentLoading(true);
    try {
      await api.post('/artist/unlock-feature', {
        feature_type: featureToUnlock,
        payment_method: paymentMethod,
        phone_number: phoneNumber
      });
      
      toast.success('Paiement initié ! Confirmez sur votre téléphone');
      
      // Poll for unlock
      setTimeout(() => {
        fetchDashboard();
        setShowPaymentModal(false);
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur paiement');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    e.preventDefault();
    
    if (!videoFile || !coverImage) {
      toast.error('Fichiers requis');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description || '');
      formData.append('price_usd', 7); // Prix fixé par admin
      formData.append('video_file', videoFile);
      formData.append('cover_image', coverImage);

      await api.post('/artist/videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('✅ Vidéo publiée ! Prix: 7$ (fixé par admin)');
      
      setShowUploadModal(false);
      setUploadForm({ title: '', description: '', price_usd: '7' });
      setVideoFile(null);
      setCoverImage(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen pb-12">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
          <span className="text-[#E50914]">Publier</span> Mon Contenu
        </h1>
        <p className="text-zinc-400 mb-8">
          {isAdmin ? 'Accès illimité gratuit (Admin)' : 'Payez 15$ (audio) ou 18$ (vidéo) pour publier'}
        </p>

        {/* Stats - Visibles pour tous */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="glass border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400">Mes Vidéos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{dashboard?.total_videos || 0}</div>
            </CardContent>
          </Card>
          
          <Card className="glass border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400">Ventes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{dashboard?.total_sales || 0}</div>
            </CardContent>
          </Card>
          
          <Card className="glass border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400">Mes Revenus (70%)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#F97316]">
                {dashboard?.total_revenue_usd?.toFixed(2) || 0}$
              </div>
              <p className="text-xs text-zinc-500 mt-1">Commission 30% déduite</p>
              <Button
                size="sm"
                onClick={() => {
                  fetchMySales();
                  setShowSalesModal(true);
                }}
                className="mt-3 w-full bg-purple-600 hover:bg-purple-700 text-xs"
              >
                📊 Voir le détail des ventes
              </Button>
            </CardContent>
          </Card>
          
          <Card className="glass border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400">❤️ J'aime</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-500">
                {dashboard?.total_likes || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Feature : Vendre Vidéos sur AGRP BOX */}
        <div className="max-w-2xl mx-auto">
          <Card className="glass border-zinc-800">
            <CardHeader className="bg-gradient-to-r from-[#E50914] to-orange-600 text-white">
              <CardTitle className="flex items-center text-2xl">
                <Video className="mr-3" size={32} />
                Vendez vos vidéos sur AGRP BOX
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <p className="text-zinc-300 text-lg">
                  Vendez vos films, séries et clips directement à vos fans.
                </p>
                
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span className="text-zinc-300">Fixez votre prix (1-100$)</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span className="text-zinc-300">Frais de dépôt: 18$</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span className="text-zinc-300">Publication automatique sur AGRP BOX</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span className="text-zinc-300">Recevez vos paiements en Mobile Money</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-orange-500 font-bold">!</span>
                    <span className="text-zinc-400 text-sm">Commission AGRP BOX: 30%</span>
                  </div>
                </div>
                
                <Button 
                  onClick={() => setShowUploadModal(true)}
                  className="w-full bg-green-600 hover:bg-green-700 py-8 text-xl font-bold"
                >
                  <Upload className="mr-3" size={24} />
                  PUBLIER UNE NOUVELLE VIDÉO
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* MES CLIPS - Liste des vidéos uploadées */}
        <Card className="glass border-zinc-800 mt-8">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <Video className="mr-2 text-[#E50914]" />
                📹 MES CLIPS ({myVideos.length})
              </span>
              <Button 
                onClick={fetchMyVideos}
                variant="outline"
                size="sm"
                className="border-zinc-700"
              >
                Actualiser
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingVideos ? (
              <div className="text-center py-8">
                <Loader2 className="animate-spin text-[#E50914] mx-auto" size={32} />
              </div>
            ) : myVideos.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-zinc-400">Aucun clip publié pour le moment</p>
                <p className="text-sm text-zinc-500 mt-2">Utilisez les boutons ci-dessus pour publier votre contenu</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myVideos.map((video, index) => (
                  <Card key={index} className="bg-zinc-900 border-zinc-800 overflow-hidden hover:border-[#E50914] transition-all">
                    <div className="relative aspect-video bg-zinc-800">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${video.cover_image_path}`}
                        alt={video.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                        }}
                      />
                      <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                        {video.file_type?.toUpperCase()}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-white truncate">{video.title}</h3>
                      <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{video.description || 'Pas de description'}</p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
                        <span className="text-[#F97316] font-bold">{video.price_usd}$</span>
                        <div className="flex items-center gap-3 text-xs text-zinc-500">
                          <span>👁️ {video.views || 0}</span>
                          <span>❤️ {video.likes || 0}</span>
                          <span>💰 {video.purchases || 0}</span>
                        </div>
                      </div>
                      {!video.is_active && (
                        <div className="mt-2 text-xs text-yellow-400">
                          ⚠️ Vidéo désactivée
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Note Admin */}
        {isAdmin && (
          <div className="mt-8 bg-green-900/20 border border-green-800 rounded-lg p-4 text-center">
            <p className="text-green-400 font-semibold">
              ✅ Vous êtes Admin - Accès gratuit illimité - 0% de commission sur vos ventes
            </p>
          </div>
        )}
      </main>

      {/* Audio Paywall Modal (25$) */}
      <Dialog open={showAudioPaywall} onOpenChange={setShowAudioPaywall}>
        <DialogContent className="glass border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-[#E50914]">
              PUBLIER SUR DYVY + SPOTIFY - 15$ FRAIS UNIQUE
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-zinc-900 border border-[#E50914] rounded-lg p-4">
              <p className="text-sm text-zinc-300 leading-relaxed text-center">
                <strong className="text-white">100% des revenus reviennent à L'ARTISTE. Commission AGRP BOX : 0%</strong>
              </p>
            </div>

            <div>
              <Label>Méthode de paiement</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="ORANGE_MONEY">Orange Money</SelectItem>
                  <SelectItem value="M_PESA">M-Pesa (Vodacom)</SelectItem>
                  <SelectItem value="AIRTEL_MONEY">Airtel Money</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Numéro de téléphone</Label>
              <Input
                type="tel"
                placeholder="+243..."
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div className="bg-zinc-900 rounded-lg p-4">
              <div className="flex justify-between font-semibold">
                <span>Montant</span>
                <span className="text-[#F97316]">15$</span>
              </div>
            </div>

            <Button
              onClick={handleAudioPaywallSubmit}
              disabled={paymentLoading}
              className="w-full bg-[#E50914] hover:bg-[#F8212C] py-6 text-lg font-semibold"
            >
              {paymentLoading ? (
                <Loader2 className="animate-spin" />
              ) : (
                'PAYER 15$'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Audio Modal (COLONNE 1) */}
      <Dialog open={showAudioUploadModal} onOpenChange={setShowAudioUploadModal}>
        <DialogContent className="glass border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>Publier un titre audio</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={async (e) => {
            e.preventDefault();
            
            if (!audioFile || !audioCover) {
              toast.error('Fichiers audio et cover requis');
              return;
            }

            setAudioUploading(true);
            try {
              const formData = new FormData();
              formData.append('title', audioForm.title);
              formData.append('artist_name', audioForm.artist_name);
              formData.append('audio_file', audioFile);
              formData.append('cover_image', audioCover);

              // Upload audio (même endpoint que vidéo, backend gère le type)
              await api.post('/artist/videos', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });

              toast.success('✅ Titre publié ! Distribution vers Spotify en cours...');
              
              setShowAudioUploadModal(false);
              setAudioForm({ title: '', artist_name: '' });
              setAudioFile(null);
              setAudioCover(null);
              fetchDashboard();
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Erreur upload');
            } finally {
              setAudioUploading(false);
            }
          }} className="space-y-4 py-4">
            <div>
              <Label>Titre du morceau *</Label>
              <Input
                value={audioForm.title}
                onChange={(e) => setAudioForm({...audioForm, title: e.target.value})}
                required
                placeholder="Ex: Mon nouveau single"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div>
              <Label>Nom de l'artiste *</Label>
              <Input
                value={audioForm.artist_name}
                onChange={(e) => setAudioForm({...audioForm, artist_name: e.target.value})}
                required
                placeholder="Ex: DJ Emergent"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div>
              <Label>Fichier Audio .WAV *</Label>
              <Input
                type="file"
                accept=".wav,audio/wav"
                onChange={(e) => setAudioFile(e.target.files[0])}
                required
                className="bg-zinc-900 border-zinc-800 text-white"
              />
              <p className="text-xs text-zinc-500 mt-1">Format WAV uniquement</p>
            </div>

            <div>
              <Label>Cover JPG *</Label>
              <Input
                type="file"
                accept="image/jpeg,image/jpg"
                onChange={(e) => setAudioCover(e.target.files[0])}
                required
                className="bg-zinc-900 border-zinc-800 text-white"
              />
              <p className="text-xs text-zinc-500 mt-1">Format JPG, min 3000x3000px</p>
            </div>

            <div className="bg-zinc-900/50 rounded-lg p-3 text-xs text-zinc-400">
              <p>✓ Publication sur AGRP BOX + Boutique</p>
              <p>✓ Distribution Spotify/Apple/Boomplay par nos équipes</p>
              <p>✓ Gestion par Label AGRP BOX</p>
            </div>

            <Button
              type="submit"
              disabled={audioUploading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {audioUploading ? <Loader2 className="animate-spin" /> : 'PUBLIER LE TITRE'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Video Modal (COLONNE 2) */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="glass border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>Publier une vidéo/audio</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleVideoUpload} className="space-y-4 py-4">
            {/* Titre en PREMIER - Bien visible */}
            <div>
              <Label className="text-white font-bold text-lg">Titre de votre Clip / Film / Série / Émission *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                required
                placeholder="Ex: Mon Nouveau Clip 2025"
                className="bg-zinc-900 border-zinc-800 text-white mt-2"
              />
            </div>

            {/* Prix LIBRE - Pas de limite admin */}
            <div>
              <Label className="text-white font-bold">Fixez votre prix (USD) - Vous êtes libre de fixer votre prix *</Label>
              <Input
                type="number"
                min="1"
                max="1000"
                value={uploadForm.price || ''}
                onChange={(e) => setUploadForm({...uploadForm, price: e.target.value})}
                placeholder="Ex: 25"
                required
                className="bg-zinc-900 border-zinc-800 text-white mt-2"
              />
              <p className="text-xs text-green-400 mt-1">✓ Vous fixez librement le prix de votre contenu (1$ à 1000$)</p>
            </div>

            <div>
              <Label>Description (optionnel)</Label>
              <textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                rows={3}
                placeholder="Décrivez votre contenu..."
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-md text-white"
              />
            </div>

            <div>
              <Label>Upload Fichier * (MP4 max 200MB / MP3 max 20MB)</Label>
              <Input
                type="file"
                accept="video/mp4,audio/mp3"
                onChange={(e) => setVideoFile(e.target.files[0])}
                required
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div>
              <Label>Image Cover *</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setCoverImage(e.target.files[0])}
                required
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div className="bg-zinc-900/50 rounded-lg p-3 text-xs text-zinc-400">
              <p>✓ Publication automatique dans /boutique</p>
              <p>✓ Commission: {isAdmin ? '0%' : '30%'} (Vous gardez {isAdmin ? '100%' : '70%'})</p>
              <p>✓ Pas de modération - Publication immédiate</p>
            </div>

            <Button
              type="submit"
              disabled={uploading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {uploading ? <Loader2 className="animate-spin" /> : 'PUBLIER AUTOMATIQUEMENT'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="glass border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              {featureToUnlock === 'distribution' 
                ? 'PUBLIER SUR DYVY + SPOTIFY - 15$ FRAIS UNIQUE'
                : 'Débloquer Vente Vidéos - 18$'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {featureToUnlock === 'distribution' ? (
              <>
                {/* Explication 15$ */}
                <div className="space-y-3">
                  <h3 className="font-bold text-lg text-[#E50914]">POURQUOI 15$ ?</h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start space-x-2">
                      <span className="text-green-500 font-bold text-lg">1.</span>
                      <span className="text-zinc-300">Publication illimitée sur AGRP BOX / Boutique</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-green-500 font-bold text-lg">2.</span>
                      <span className="text-zinc-300">Mise en ligne par nos équipes sur Spotify, Apple Music, Boomplay, Deezer</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-green-500 font-bold text-lg">3.</span>
                      <span className="text-zinc-300">Gestion de ton titre par le Label AGRP BOX</span>
                    </div>
                  </div>

                  <div className="bg-zinc-900 border border-[#E50914] rounded-lg p-4 mt-4">
                    <p className="text-xs text-zinc-300 leading-relaxed">
                      <strong className="text-[#E50914]">IMPORTANT:</strong> En payant 15$, vous publiez sur AGRP BOX et Spotify/Apple/Boomplay. 
                      100% des revenus reviennent à L'ARTISTE. Commission AGRP BOX : 0%
                    </p>
                  </div>
                </div>
              </>
            ) : null}

            <div>
              <Label>Méthode de paiement</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="ORANGE_MONEY">Orange Money</SelectItem>
                  <SelectItem value="M_PESA">M-Pesa (Vodacom)</SelectItem>
                  <SelectItem value="AIRTEL_MONEY">Airtel Money</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Numéro de téléphone</Label>
              <Input
                type="tel"
                placeholder="+243..."
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>


      {/* Modal Transparence des Ventes */}
      <Dialog open={showSalesModal} onOpenChange={setShowSalesModal}>
        <DialogContent className="glass border-zinc-800 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              📊 Transparence des Ventes - Mes Revenus
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {mySales.length === 0 ? (
              <p className="text-zinc-400 text-center py-8">Aucune vente pour le moment</p>
            ) : (
              <>
                <div className="bg-zinc-900 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-zinc-400 text-sm">Total des ventes</p>
                      <p className="text-2xl font-bold text-white">{mySales.length}</p>
                    </div>
                    <div>
                      <p className="text-zinc-400 text-sm">Total gagné (70%)</p>
                      <p className="text-2xl font-bold text-[#F97316]">
                        {mySales.reduce((sum, sale) => sum + (sale.my_commission_usd || 0), 0).toFixed(2)}$
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {mySales.map((sale, index) => (
                    <div key={index} className="border border-zinc-800 rounded-lg p-4 bg-zinc-900/50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-white">{sale.content_title}</h3>
                          <p className="text-sm text-zinc-400">
                            Acheté par: <span className="text-white">{sale.buyer_name}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#F97316]">
                            +{sale.my_commission_usd?.toFixed(2)}$
                          </p>
                          <p className="text-xs text-zinc-500">Ma part (70%)</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-3 text-xs text-zinc-400 mt-3 pt-3 border-t border-zinc-800">
                        <span>💰 Montant total: {sale.amount_usd}$</span>
                        <span>💳 {sale.payment_method}</span>
                        <span>📅 {new Date(sale.purchase_date).toLocaleString('fr-FR')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

            <div className="bg-zinc-900 rounded-lg p-4">
              <div className="flex justify-between font-semibold">
                <span>Montant</span>
                <span className="text-[#F97316]">
                  {featureToUnlock === 'distribution' ? '15' : '18'}$
                </span>
              </div>
            </div>

            <Button
              onClick={handlePaymentSubmit}
              disabled={paymentLoading}
              className="w-full bg-[#E50914] hover:bg-[#F8212C] py-6 text-lg font-semibold"
            >
              {paymentLoading ? (
                <Loader2 className="animate-spin" />
              ) : (
                `PAYER ${featureToUnlock === 'distribution' ? '15' : '18'}$`
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtistDashboard;
